import Component from '@glimmer/component';
import formatDate from 'ember-intl/helpers/format-date';
import t from 'ember-intl/helpers/t';
import { not } from 'ember-truth-helpers';
import eq from 'ember-truth-helpers/helpers/eq';
import PhoneLink from 'parog-web/components/phone-link';
import ProductName from 'parog-web/components/product-name';
import type Patient from 'parog-web/models/patient';
import TitledLabel from 'ui-kit/components/titled-label';

export interface Signature {
  Args: {
    patient: Patient;
    propertyName: string;
    description: string;
    format?: 'titleize' | 'email' | 'phone' | 'code' | 'gender' | 'birthday';
    shouldDisplayEmpty?: boolean;
  };
  Blocks: { copyButton?: [] };
}

export default class LabeledPatientInformation extends Component<Signature> {
  get hasValue(): boolean {
    return Boolean(this.propertyValue);
  }

  get propertyValue(): string {
    const value = this.args.patient?.[this.args.propertyName];
    if (this.args.propertyName.includes('.')) {
      return this.#getNestedProperty(this.args.patient, this.args.propertyName);
    }
    if (this.args.format === 'titleize' && value) {
      return this.#titleize(value);
    }
    return value;
  }

  get shouldDisplay(): boolean | undefined {
    return this.hasValue || this.args.shouldDisplayEmpty;
  }

  #getNestedProperty(obj: Patient, path: string): string {
    return path
      .split('.')
      .reduce((acc, key) => acc && acc[key], obj as unknown as string);
  }

  #titleize(string: string): string {
    return string.toLowerCase().replace(/(?:^|\s|-|\/)\S/g, function (m) {
      return m.toUpperCase();
    });
  }

  <template>
    {{#if this.shouldDisplay}}
      <div class='column'>
        <TitledLabel data-test-label={{@propertyName}}>
          <:title>
            {{#if (eq @format 'code')}}
              {{@description}}
              <ProductName />
            {{else}}
              {{@description}}
            {{/if}}
            {{yield to='copyButton'}}
          </:title>
          <:body>
            {{#if (not this.hasValue)}}
              -
            {{else if (eq @format 'email')}}
              <a href='mailto:{{this.propertyValue}}'>{{this.propertyValue}}</a>
            {{else if (eq @format 'gender')}}
              {{t 'patient.gender' gender=this.propertyValue}}
            {{else if (eq @format 'birthday')}}
              {{formatDate this.propertyValue format='DD/MM/YYYY'}}
            {{else if (eq @format 'phone')}}
              <PhoneLink @phone={{this.propertyValue}} />
            {{else}}
              {{this.propertyValue}}
            {{/if}}
          </:body>
        </TitledLabel>
      </div>
    {{/if}}
  </template>
}
